import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, MessageSquareIcon, UserCheckIcon, CalendarIcon } from 'lucide-react';
export function Hero() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };
  const floatingCardVariants = {
    hidden: {
      x: 50,
      opacity: 0,
      scale: 0.9
    },
    visible: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 80,
        damping: 12
      }
    }
  };
  return <section className="relative bg-white overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32" data-id="element-413" data-mp="382p91mw8pvpw">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3" data-id="element-414" data-mp="3j9u0yz9q1olt">
        <div className="w-[600px] h-[600px] rounded-full bg-red-50 opacity-50 blur-3xl" data-id="element-415" data-mp="3o7vl7nivvtyv"></div>
      </div>
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3" data-id="element-416" data-mp="399bahlnnj9ux">
        <div className="w-[400px] h-[400px] rounded-full bg-gray-50 opacity-80 blur-3xl" data-id="element-417" data-mp="3e8wielksusjs"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-418" data-mp="30hrev8eb9g46">
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center" data-id="element-419" data-mp="358hmyte4fx2k">
          {/* Left Content */}
          <motion.div className="lg:col-span-6 text-center lg:text-left mb-16 lg:mb-0" variants={containerVariants} initial="hidden" animate="visible" data-id="element-420" data-mp="2qkdcudt4wtve">
            <motion.div variants={itemVariants} className="mb-4" data-id="element-421" data-mp="2hix03zeukfo0">
              <span className="inline-block py-1 px-3 rounded-full bg-red-100 text-red-700 text-sm font-semibold tracking-wide" data-id="element-422" data-mp="2lkqz839e9bm2">
                Marketing Experts. Driven by Results.
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight mb-6 leading-tight" data-id="element-423" data-mp="nb1kz5gpvtwy">
              Not Hitting Your Local{' '}
              <span className="text-red-600" data-id="element-424" data-mp="deylk7d929dp">SEO/PPC</span> Marketing Goals?
            </motion.h1>

            <motion.p variants={itemVariants} className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-425" data-mp="1b04cd515l14n">
              We are Marketing Experts at Driving & Turning Traffic into
              Conversions with a Focus on Building Customer Loyalty & Retention!
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-426" data-mp="3lt75ys5mbtus">
              <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-md font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-427" data-mp="1x9b3owgzwtag">
                SCHEDULE FREE SESSION
              </button>
              <button className="bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 px-8 py-4 rounded-md font-bold text-lg transition-all shadow-sm hover:shadow-md" data-id="element-428" data-mp="269r7k71ni2fu">
                Learn More
              </button>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center lg:justify-start space-x-4 text-sm text-gray-500" data-id="element-429" data-mp="2kyvntiovxwlb">
              <div className="flex -space-x-2" data-id="element-430" data-mp="2oynzi49ksiuw">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center overflow-hidden" data-id="element-431" data-mp="2txhclvkya7uj">
                    <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User avatar" className="w-full h-full object-cover" data-id="element-432" data-mp="zndq9dhh3e5d" />
                  </div>)}
              </div>
              <div className="flex flex-col" data-id="element-433" data-mp="e8scvec7iwge">
                <div className="flex items-center text-yellow-400" data-id="element-434" data-mp="99xm9q8003lq">
                  {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-435" data-mp="3hrby3wwgx21p" />)}
                </div>
                <span data-id="element-436" data-mp="3csifjq2bqkuo">Trusted by 200+ businesses</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Device Mockup & Floating Cards */}
          <div className="lg:col-span-6 relative h-[500px] sm:h-[600px] flex items-center justify-center" data-id="element-437" data-mp="3moc2829rbuq6">
            {/* Main Device Mockup (CSS only) */}
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="relative w-[280px] sm:w-[320px] h-[580px] sm:h-[640px] bg-gray-900 rounded-[3rem] border-[8px] border-gray-800 shadow-2xl overflow-hidden z-10" data-id="element-438" data-mp="2u0pq25mgrhrp">
              {/* Notch */}
              <div className="absolute top-0 inset-x-0 h-6 bg-gray-800 rounded-b-3xl w-40 mx-auto z-20" data-id="element-439" data-mp="1s8vk6f9ep9dp"></div>

              {/* Screen Content */}
              <div className="absolute inset-0 bg-gray-50 pt-12 px-4 pb-6 flex flex-col" data-id="element-440" data-mp="1x65fgbjh8f9g">
                <div className="flex justify-between items-center mb-6" data-id="element-441" data-mp="1j874zh8a12z8">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center" data-id="element-442" data-mp="1n8r06f9ahwiq">
                    <span className="text-red-600 font-bold text-xs" data-id="element-443" data-mp="7sigumjadxwu">ad</span>
                  </div>
                  <div className="w-24 h-2 bg-gray-200 rounded-full" data-id="element-444" data-mp="bvoifgvgf0wz"></div>
                </div>

                <div className="space-y-4 flex-1" data-id="element-445" data-mp="2eg924jn9437t">
                  <div className="w-full h-32 bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-id="element-446" data-mp="29ih7viz4w50g">
                    <div className="w-1/2 h-4 bg-gray-200 rounded mb-4" data-id="element-447" data-mp="24rhdolheg11n"></div>
                    <div className="w-full h-2 bg-gray-100 rounded mb-2" data-id="element-448" data-mp="24ped57imepio"></div>
                    <div className="w-3/4 h-2 bg-gray-100 rounded" data-id="element-449" data-mp="1zse88zxibst5"></div>
                  </div>
                  <div className="w-full h-24 bg-red-50 rounded-xl border border-red-100 p-4" data-id="element-450" data-mp="1vsni8yft1gpo">
                    <div className="w-1/3 h-4 bg-red-200 rounded mb-3" data-id="element-451" data-mp="1qqerm8qemqqq"></div>
                    <div className="w-full h-8 bg-white rounded-md" data-id="element-452" data-mp="1lty0d5nwrw9f"></div>
                  </div>
                  <div className="w-full h-32 bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-id="element-453" data-mp="1guv8kmwxvmje">
                    <div className="flex space-x-3 mb-4" data-id="element-454" data-mp="3g5uuj6dqjg86">
                      <div className="w-10 h-10 rounded-full bg-gray-200" data-id="element-455" data-mp="3b7td0pvyjoyt"></div>
                      <div className="flex-1" data-id="element-456" data-mp="3b6075ct658rv">
                        <div className="w-1/2 h-3 bg-gray-200 rounded mb-2" data-id="element-457" data-mp="3jpmxo3yzqdr0"></div>
                        <div className="w-1/3 h-2 bg-gray-100 rounded" data-id="element-458" data-mp="3npx57l2flc7z"></div>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded mb-2" data-id="element-459" data-mp="3sigjylcuuded"></div>
                    <div className="w-5/6 h-2 bg-gray-100 rounded" data-id="element-460" data-mp="1b3znxgodju8"></div>
                  </div>
                </div>

                {/* Bottom Nav */}
                <div className="h-14 bg-white rounded-2xl shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] flex justify-around items-center px-2 mt-auto" data-id="element-461" data-mp="30s2dha7683cq">
                  <div className="w-8 h-8 rounded-full bg-red-100" data-id="element-462" data-mp="39qe8dof2lnaz"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-463" data-mp="3eph4ukgzil7c"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-464" data-mp="3ennxf2yjrwya"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-465" data-mp="ozx0a788sfv6"></div>
                </div>
              </div>
            </motion.div>

            {/* Floating Cards */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="absolute inset-0 z-20 pointer-events-none" data-id="element-466" data-mp="1f2smanybhu9j">
              {/* Card 1: New Lead */}
              <motion.div variants={floatingCardVariants} className="absolute top-[15%] -left-4 sm:-left-12 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-64 sm:w-72 rotate-[-2deg]" data-id="element-467" data-mp="1ovgcv0ypf1zt">
                <div className="bg-green-100 p-2 rounded-lg flex-shrink-0" data-id="element-468" data-mp="1tryevt2ngm8g">
                  <MessageSquareIcon className="w-5 h-5 text-green-600" data-id="element-469" data-mp="27r6l35x8cum5" />
                </div>
                <div data-id="element-470" data-mp="22u7qyndkgnny">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-471" data-mp="22to6aywj7l68">
                    New Lead
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-472" data-mp="8fdhurjumt0z">
                    Sarah M.
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-473" data-mp="2k27bahz3bko5">
                    Requested a quote for SEO services
                  </p>
                </div>
              </motion.div>

              {/* Card 2: Review */}
              <motion.div variants={floatingCardVariants} className="absolute top-[40%] -right-8 sm:-right-16 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-60 sm:w-64 rotate-[3deg]" data-id="element-474" data-mp="2b9nihy5hcab8">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0" data-id="element-475" data-mp="26c3tc378ud4i">
                  <StarIcon className="w-5 h-5 text-blue-600" data-id="element-476" data-mp="1sbkr5cpgm1a9" />
                </div>
                <div data-id="element-477" data-mp="1wcwipnrw8wl3">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-478" data-mp="1wcd16865oa7l">
                    Google Review
                  </p>
                  <div className="flex text-yellow-400 mb-1" data-id="element-479" data-mp="3mn7szj6rr205">
                    {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-3 h-3 fill-current" data-id="element-480" data-mp="3qokz7udrx77l" />)}
                  </div>
                  <p className="text-xs text-gray-500 italic" data-id="element-481" data-mp="fubunwkb9hxk">
                    "Amazing results in just 2 months!"
                  </p>
                </div>
              </motion.div>

              {/* Card 3: Conversion */}
              <motion.div variants={floatingCardVariants} className="absolute bottom-[30%] -left-8 sm:-left-16 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-64 sm:w-72 rotate-[-1deg]" data-id="element-482" data-mp="65tspc0ap4sc">
                <div className="bg-red-100 p-2 rounded-lg flex-shrink-0" data-id="element-483" data-mp="kug9zj9hg6m7">
                  <UserCheckIcon className="w-5 h-5 text-red-600" data-id="element-484" data-mp="ov9x4af3phe1" />
                </div>
                <div data-id="element-485" data-mp="ox4l5xlo5pmc">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-486" data-mp="35je3deaazb2c">
                    New Conversion
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-487" data-mp="39mtzm9ign9ei">
                    Form Submitted
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-488" data-mp="2x2wzzwtjuxwa">
                    Campaign: Local PPC Q3
                  </p>
                </div>
              </motion.div>

              {/* Card 4: Appointment */}
              <motion.div variants={floatingCardVariants} className="absolute bottom-[10%] -right-4 sm:-right-12 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-56 sm:w-64 rotate-[2deg]" data-id="element-489" data-mp="371cgl5uyaorj">
                <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0" data-id="element-490" data-mp="2eaknih96mg8f">
                  <CalendarIcon className="w-5 h-5 text-purple-600" data-id="element-491" data-mp="2t2mjvrc1mr3p" />
                </div>
                <div data-id="element-492" data-mp="2o90hqt3ia5m5">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-493" data-mp="2o8qn2s7inaa4">
                    Appointment
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-494" data-mp="1vkuu9yl2i2im">
                    Ashley C.
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-495" data-mp="2l9jqyal1839d">
                    Strategy Session @ 2PM
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}